import React, { useCallback, useMemo } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import { Box } from '@mui/material';

import { formatDateISO } from '~/services/TimeService';
import VendorsService from '~/services/VendorsService';

import { enhanceErrorPaymentSchema, errorPaymentUiSchema, errorPaymentWidgets } from './constants';

interface Props {
  schema: RJSFSchema;
  id_adjustment: string;
  date_of_adjustment: string;
  fk_offer: string;
  fk_vendor: string;
  currency: string;
  amount: string;
  reason?: string | null;
  le_booking_number?: string | null;
  description: string;
  onErrorPaymentUpdated: (errorPayment: Record<string, unknown>) => void;
}

export function ErrorPaymentsUpdateForm({
  schema,
  id_adjustment,
  date_of_adjustment,
  fk_offer,
  fk_vendor,
  currency,
  amount,
  reason,
  le_booking_number,
  description,
  onErrorPaymentUpdated,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const enhancedSchema = useMemo(() => enhanceErrorPaymentSchema(schema), [schema]);

  const formData = useMemo(
    () => ({
      id_adjustment,
      date_of_adjustment: formatDateISO(date_of_adjustment),
      fk_offer,
      fk_vendor,
      currency,
      amount,
      reason: reason || '',
      le_booking_number: le_booking_number || '',
      description,
    }),
    [id_adjustment, date_of_adjustment, fk_offer, fk_vendor, currency, amount, reason, le_booking_number, description],
  );

  const validationHandler = useCallback((data, errors) => {
    const parsedAmount = Number.parseFloat(data.amount);
    if (!Number.isFinite(parsedAmount)) {
      errors.amount.addError('should be a number');
    }
    return errors;
  }, []);

  const submitHandler = useCallback(
    async (e: IChangeEvent) => {
      try {
        const res = await VendorsService.updateErrorPayment(e.formData);
        enqueueSnackbar('Error payment updated', { variant: 'success' });
        onErrorPaymentUpdated(res.result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        enqueueSnackbar(`Can't update error payment: ${errorMessage}`, { variant: 'error' });
      }
    },
    [onErrorPaymentUpdated, enqueueSnackbar],
  );

  return (
    <Box id="errorpayments-form" mb={2}>
      <Form
        schema={enhancedSchema}
        uiSchema={errorPaymentUiSchema}
        widgets={errorPaymentWidgets}
        onSubmit={submitHandler}
        customValidate={validationHandler}
        validator={validator}
        formData={formData}
      />
    </Box>
  );
}
